import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

// Use the prebuilt worker copied to public/ by scripts/copy-maplibre-worker.js: the copy
// bundled with maplibre-gl is transpiled by CRA and breaks GeoJSON sources.
maplibregl.setWorkerUrl(`${process.env.PUBLIC_URL || ""}/maplibre-gl-csp-worker.js`);

// Composant principal MapLibre pour gérer différents types de cartes et points.
// turbineDashboard only:
// - cameraAzimuth / cameraFieldOfView: real viewing direction of the selected alert's camera
//   (degrees from true north / degrees). When the azimuth is set, a cone is drawn instead of
//   the fixed orientation sector image.
// - detection: { lat, lng, distance, label } estimated position of the selected detection.
const MDMapLibre = React.memo(({ mapType, points, cameraOrientation = 1, circleRadius = 150,
    cameraAzimuth = null, cameraFieldOfView = null, detection = null }) => {
    // Référence du container HTML où sera rendu la carte.
    const mapContainer = useRef(null);

    // Référence de l'instance MapLibre (pour éviter de la recréer).
    const mapRef = useRef(null);

    // Référence aux markers pour les supprimer quand on met à jour.
    const markersRef = useRef([]);

    // Camera name label drawn on the real-azimuth cone (turbineDashboard).
    const cameraLabelRef = useRef(null);

    // URL de l'image selon orientation de l'alerte levée.
    const [cameraUrl, setCameraUrl] = useState("/images/cible_c1_nord.png");

    // Stocke les bounds initiaux pour resetView.
    const [initialBounds, setInitialBounds] = useState(null);

    const navigate = useNavigate();

    // Met à jour l'image de la caméra selon l'orientation.
    useEffect(() => {
        const cameraUrls = {
            1: "/images/cible_c1_nord.png",
            2: "/images/cible_c2_nord-est.png",
            3: "/images/cible_c3_est.png",
            4: "/images/cible_c4_sud-est.png",
            5: "/images/cible_c5_sud.png",
            6: "/images/cible_c6_sud-ouest.png",
            7: "/images/cible_c7_ouest.png",
            8: "/images/cible_c8_nord-ouest.png",
        };
        setCameraUrl(cameraUrls[cameraOrientation] || cameraUrls[1]);
    }, [cameraOrientation]);

    // Calcul du zoom selon le rayon en mètres (le zoom décroît quand le rayon augmente).
    const getZoomForRadius = (radius) => {
        const baseRadius = 150; // rayon de référence
        const baseZoom = 16; // zoom de référence pour ce rayon
        return Math.min(Math.max(Math.floor(baseZoom - Math.log2(radius / baseRadius)), 0), 19);
    };

    // Default cone width when the camera field of view is unknown (degrees).
    const DEFAULT_FIELD_OF_VIEW = 60;
    const hasAzimuth = Number.isFinite(cameraAzimuth);
    const hasDetection = detection && Number.isFinite(detection.lat) && Number.isFinite(detection.lng);

    // Ground point at `distanceMeters` and `bearingDeg` (clockwise from north) from (lat, lng).
    const destinationPoint = (lat, lng, distanceMeters, bearingDeg) => {
        const metersPerDegree = 111320;
        const b = (bearingDeg * Math.PI) / 180;
        return [
            lng + (distanceMeters * Math.sin(b)) / (metersPerDegree * Math.cos((lat * Math.PI) / 180)),
            lat + (distanceMeters * Math.cos(b)) / metersPerDegree,
        ];
    };

    // Run `fn` as soon as the map style can take sources/layers.
    // - isStyleLoaded() is also false while tiles are loading;
    // - `load` waits for every tile and sprite, and never fires if one of them hangs.
    // The style is usable once it is parsed (`style.load`). Calls made before that are queued
    // by key so that only the latest overlay / detection drawing runs.
    const styleReadyRef = useRef(false);
    const pendingDrawsRef = useRef({});
    const runWhenStyleReady = (key, fn) => {
        if (styleReadyRef.current) fn();
        else pendingDrawsRef.current[key] = fn;
    };

    // GeoJSON disc of `radiusMeters` around (lat, lng).
    const getDisc = (lat, lng, radiusMeters) => {
        const ring = [];
        for (let i = 0; i <= 64; i++) ring.push(destinationPoint(lat, lng, radiusMeters, (360 * i) / 64));
        return { type: "Feature", geometry: { type: "Polygon", coordinates: [ring] }, properties: {} };
    };

    // GeoJSON cone centred on the camera azimuth, from the turbine to `radiusMeters`.
    const getCameraCone = (lat, lng, radiusMeters, azimuthDeg, fieldOfViewDeg) => {
        const half = (Number.isFinite(fieldOfViewDeg) ? fieldOfViewDeg : DEFAULT_FIELD_OF_VIEW) / 2;
        const ring = [[lng, lat]];
        for (let i = 0; i <= 32; i++) {
            ring.push(destinationPoint(lat, lng, radiusMeters, azimuthDeg - half + (2 * half * i) / 32));
        }
        ring.push([lng, lat]);
        return { type: "Feature", geometry: { type: "Polygon", coordinates: [ring] }, properties: {} };
    };

    // Convertit un rayon en coordonnées pour placer l'orientation des caméras.
    const getImageCoordinates = (lat, lng, radiusMeters) => {
        const metersPerDegree = 111320; // approx m par degré latitude

        const deltaLat = radiusMeters / metersPerDegree;
        const deltaLng = radiusMeters / (metersPerDegree * Math.cos((lat * Math.PI) / 180));

        return [
            [lng - deltaLng, lat + deltaLat], // top-left
            [lng + deltaLng, lat + deltaLat], // top-right
            [lng + deltaLng, lat - deltaLat], // bottom-right
            [lng - deltaLng, lat - deltaLat], // bottom-left
        ];
    };

    // Initialisation de la carte MapLibre.
    useEffect(() => {
        if (mapRef.current || !mapContainer.current || points.length === 0) return;

        // Valeurs par défaut.
        let center = [-0.09, 51.505];
        let zoom = 13;

        if (points.length === 1) {
            center = [points[0].lng, points[0].lat];

            if (mapType === "turbineDashboard") {
                // Calcul des bounds incluant l'image overlay.
                const imageCoords = getImageCoordinates(points[0].lat, points[0].lng, circleRadius);
                const imageBounds = new maplibregl.LngLatBounds();
                imageCoords.forEach(([lng, lat]) => imageBounds.extend([lng, lat]));
                imageBounds.extend([points[0].lng, points[0].lat]);
                center = [imageBounds.getCenter().lng, imageBounds.getCenter().lat];
                zoom = Math.min(getZoomForRadius(circleRadius + 50), 19); // zoom légèrement plus large
            } else {
                zoom = getZoomForRadius(circleRadius);
            }
        } else {
            // Plusieurs points : calcul des bounds automatiques.
            const bounds = new maplibregl.LngLatBounds();
            points.forEach(p => bounds.extend([p.lng, p.lat]));
            center = [bounds.getCenter().lng, bounds.getCenter().lat];
        }

        // Création de la carte MapLibre.
        const map = new maplibregl.Map({
            container: mapContainer.current,
            style: "https://tiles.openfreemap.org/styles/liberty",
            center,
            zoom,
        });

        // Ajout des contrôles classiques (navigation + scale).
        map.addControl(new maplibregl.NavigationControl(), "top-left");
        map.addControl(new maplibregl.ScaleControl({ unit: "metric", maxWidth: 100 }), "bottom-left");

        mapRef.current = map;
        const onStyleReady = () => {
            if (styleReadyRef.current) return;
            styleReadyRef.current = true;
            const pending = pendingDrawsRef.current;
            pendingDrawsRef.current = {};
            Object.values(pending).forEach((fn) => fn());
        };
        map.once("style.load", onStyleReady);
        map.once("load", onStyleReady);

        // Resize pour s'assurer que la carte occupe bien le container.
        setTimeout(() => map.resize(), 100);
        window.addEventListener("resize", () => map.resize());
    }, [points, mapType, circleRadius]);

    // Gestion des markers et popups.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || points.length === 0) return;

        // Supprime les anciens markers.
        markersRef.current.forEach((m) => m.remove());
        markersRef.current = [];

        const bounds = new maplibregl.LngLatBounds();

        points.forEach((point) => {
            const el = document.createElement("img");
            el.src = mapType === "parkList"
                ? "/images/park_marker.png"
                : "/images/mapLeaflet_marker_shadow.png";

            // Style du marker.
            el.style.width = mapType === "parkList" ? "20px" : "40px";
            el.style.height = mapType === "parkList" ? "28px" : "40px";
            el.style.cursor = "pointer";
            el.style.transform = "translate(-50%, -100%)";

            let marker;

            if (mapType === "turbineDashboard") {
                // Marker simple, pas de popup.
                marker = new maplibregl.Marker({ element: el, anchor: "center" })
                    .setLngLat([point.lng, point.lat])
                    .addTo(map);
            } else {
                // Popup personnalisée.
                const popupContent = document.createElement("div");
                popupContent.style.display = "flex";
                popupContent.style.alignItems = "center";
                popupContent.style.justifyContent = "space-between";
                popupContent.style.padding = "2px 3px";
                popupContent.style.fontFamily = "Arial, sans-serif";
                popupContent.style.color = "#344767";
                popupContent.style.whiteSpace = "pre-line";

                // Wrapper du texte.
                const textWrapper = document.createElement("div");
                textWrapper.style.cursor = "pointer";

                point.popupText.split("\n").forEach((line, i) => {
                    const span = document.createElement("span");
                    span.textContent = line;
                    span.style.display = "block";
                    span.style.fontSize = i === 0 ? "13px" : "10px";
                    span.style.fontWeight = i === 0 ? "bold" : "normal";
                    textWrapper.appendChild(span);
                });

                // Navigation au click sur le texte.
                textWrapper.onclick = () => {
                    const { pathname } = window.location;

                    if (pathname === "/parkList") {
                        localStorage.setItem("selectedParkID", point.id);
                        navigate("/parkDashboard");
                    } else if (pathname === "/parkDashboard") {
                        localStorage.setItem("selectedTurbineID", point.id);
                        navigate("/turbineDashboard");
                    }
                };

                popupContent.appendChild(textWrapper);

                // Bouton close.
                let popup;
                const closeBtn = document.createElement("div");
                closeBtn.className = "popup-close-btn";
                closeBtn.innerHTML = "&times;";
                closeBtn.onclick = (e) => {
                    e.stopPropagation();
                    popup.remove();
                };
                popupContent.appendChild(closeBtn);

                // Création du popup MapLibre.
                popup = new maplibregl.Popup({
                    anchor: "bottom",
                    offset: 25,
                    closeButton: false,
                    className: "custom-popup"
                }).setDOMContent(popupContent);

                // Ajustement de la carte pour que la popup soit visible.
                popup.on("open", () => {
                    const mapCanvas = map.getContainer().getBoundingClientRect();
                    const pixelPos = map.project([point.lng, point.lat]);

                    const popupHeight = 100;
                    const popupWidth = 200;

                    let offsetX = 0;
                    let offsetY = 0;

                    if (pixelPos.x + popupWidth / 2 > mapCanvas.width) {
                        offsetX = pixelPos.x + popupWidth / 2 - mapCanvas.width + 10;
                    } else if (pixelPos.x - popupWidth / 2 < 0) {
                        offsetX = pixelPos.x - popupWidth / 2;
                    }

                    if (pixelPos.y - popupHeight < 0) {
                        offsetY = pixelPos.y - popupHeight - 10;
                    }

                    if (offsetX !== 0 || offsetY !== 0) {
                        map.panBy([offsetX, offsetY], { easing: t => t });
                    }
                });

                const anchorType = mapType === "parkList" ? "bottom" : "center";

                marker = new maplibregl.Marker({ element: el, anchor: anchorType })
                    .setLngLat([point.lng, point.lat])
                    .setPopup(popup)
                    .addTo(map);
            }

            markersRef.current.push(marker);
            bounds.extend([point.lng, point.lat]);
        });

        setInitialBounds(bounds.isEmpty() ? null : bounds);

        // Ajustement de la vue pour un ou plusieurs points.
        if (points.length > 1) {
            map.fitBounds(bounds, { padding: 50 });
        } else if (points.length === 1) {
            if (mapType === "turbineDashboard") {
                const imageCoords = getImageCoordinates(points[0].lat, points[0].lng, circleRadius);
                const imageBounds = new maplibregl.LngLatBounds();
                imageCoords.forEach(([lng, lat]) => imageBounds.extend([lng, lat]));
                imageBounds.extend([points[0].lng, points[0].lat]);
                map.fitBounds(imageBounds, { padding: 20 });
            }
        }

    }, [points, mapType, cameraUrl, navigate]);

    // Ajout du style CSS pour les popups.
    useEffect(() => {
        const style = document.createElement("style");
        style.innerHTML = `
            .custom-popup .mapboxgl-popup-content {
                border-radius: 5px;
                box-shadow: 0 4px 15px rgba(0,0,0,0.25);
                background: white;
                padding: 0;
            }
            .popup-close-btn {
                font-size: 18px;
                font-weight: bold;
                color: #888;
                cursor: pointer;
                margin-left: 8px;
            }
            .popup-close-btn:hover {
                color: #000;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    // Ajout overlay image sur turbineDashboard.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || mapType !== "turbineDashboard" || points.length === 0) return;

        runWhenStyleReady("overlay", addOverlay);

        function addOverlay() {
            points.forEach((point, index) => {
                const sourceId = `camera-source-${index}`;
                const layerId = `camera-layer-${index}`;
                const coneSourceId = `camera-cone-source-${index}`;
                const coneLayerId = `camera-cone-layer-${index}`;
                const coneLineId = `camera-cone-line-${index}`;

                // Supprimer l'ancien overlay si existant.
                [layerId, coneLayerId, coneLineId].forEach((id) => { if (map.getLayer(id)) map.removeLayer(id); });
                [sourceId, coneSourceId].forEach((id) => { if (map.getSource(id)) map.removeSource(id); });

                const beforeId = map.getStyle().layers.find(l => l.type === "symbol")?.id;

                [1, 2, 3].forEach((k) => {
                    const id = `perimeter-${index}-${k}`;
                    if (map.getLayer(id)) map.removeLayer(id);
                    if (map.getSource(id)) map.removeSource(id);
                });
                if (cameraLabelRef.current) {
                    cameraLabelRef.current.remove();
                    cameraLabelRef.current = null;
                }

                // Real camera direction known: same perimeter discs as the sector image, with the
                // camera sector highlighted at its real azimuth instead of a fixed 45-degree sector.
                if (hasAzimuth) {
                    [[1, 0.22], [2 / 3, 0.18], [1 / 3, 0.18]].forEach(([scale, opacity], k) => {
                        const id = `perimeter-${index}-${k + 1}`;
                        map.addSource(id, { type: "geojson", data: getDisc(point.lat, point.lng, circleRadius * scale) });
                        map.addLayer({ id, type: "fill", source: id,
                            paint: { "fill-color": "#4fc3f7", "fill-opacity": opacity } }, beforeId);
                    });
                    map.addSource(coneSourceId, {
                        type: "geojson",
                        data: getCameraCone(point.lat, point.lng, circleRadius, cameraAzimuth, cameraFieldOfView),
                    });
                    map.addLayer({ id: coneLayerId, type: "fill", source: coneSourceId,
                        paint: { "fill-color": "#29b6f6", "fill-opacity": 0.55 } }, beforeId);
                    map.addLayer({ id: coneLineId, type: "line", source: coneSourceId,
                        paint: { "line-color": "#81d4fa", "line-width": 2 } }, beforeId);

                    // Camera name in the outer part of its cone, styled like the sector image labels.
                    const label = document.createElement("div");
                    label.textContent = `C${cameraOrientation}`;
                    label.style.color = "#ffffff";
                    label.style.fontFamily = "Arial, sans-serif";
                    label.style.fontSize = "18px";
                    label.style.fontWeight = "bold";
                    label.style.textShadow = "0 0 3px rgba(2,136,209,0.9)";
                    label.style.pointerEvents = "none";
                    cameraLabelRef.current = new maplibregl.Marker({ element: label, anchor: "center" })
                        .setLngLat(destinationPoint(point.lat, point.lng, circleRadius * 0.8, cameraAzimuth))
                        .addTo(map);
                    return;
                }

                map.addSource(sourceId, {
                    type: "image",
                    url: cameraUrl,
                    coordinates: getImageCoordinates(point.lat, point.lng, circleRadius),
                });

                map.addLayer(
                    {
                        id: layerId,
                        type: "raster",
                        source: sourceId,
                        paint: { "raster-opacity": 0.85 },
                    },
                    map.getStyle().layers.find(l => l.type === "symbol")?.id // placer sous les markers
                );
            });
        }
    }, [mapType, points, cameraUrl, circleRadius, hasAzimuth, cameraAzimuth, cameraFieldOfView, cameraOrientation]);

    // Selected detection position (turbineDashboard): marker + dashed line from the turbine.
    const detectionMarkerRef = useRef(null);
    const detectionPopupRef = useRef(null);
    useEffect(() => {
        const map = mapRef.current;
        if (!map || mapType !== "turbineDashboard" || points.length === 0) return;

        const lineSourceId = "detection-line-source";
        const lineLayerId = "detection-line-layer";

        const update = () => {
            if (detectionMarkerRef.current) {
                detectionMarkerRef.current.remove();
                detectionMarkerRef.current = null;
            }
            if (detectionPopupRef.current) {
                detectionPopupRef.current.remove();
                detectionPopupRef.current = null;
            }
            if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId);
            if (map.getSource(lineSourceId)) map.removeSource(lineSourceId);
            if (!hasDetection) return;

            map.addSource(lineSourceId, {
                type: "geojson",
                data: { type: "Feature", properties: {}, geometry: { type: "LineString",
                    coordinates: [[points[0].lng, points[0].lat], [detection.lng, detection.lat]] } },
            });
            map.addLayer({ id: lineLayerId, type: "line", source: lineSourceId,
                paint: { "line-color": "#d32f2f", "line-width": 2, "line-dasharray": [2, 2] } });

            const el = document.createElement("div");
            el.style.width = "14px";
            el.style.height = "14px";
            el.style.borderRadius = "50%";
            el.style.background = "#d32f2f";
            el.style.border = "2px solid #fff";
            el.style.boxShadow = "0 0 4px rgba(0,0,0,0.5)";

            const popupText = [detection.label, Number.isFinite(detection.distance) ? `${detection.distance} m` : null]
                .filter(Boolean).join(" · ");
            // Label always visible (not bound to a click): activity and distance, placed on the side
            // of the point away from the turbine so it does not cover the camera cone.
            const dx = (detection.lng - points[0].lng) * Math.cos((points[0].lat * Math.PI) / 180);
            const dy = detection.lat - points[0].lat;
            const bearing = ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360;
            const anchor = bearing >= 315 || bearing < 45 ? "bottom" : bearing < 135 ? "left" : bearing < 225 ? "top" : "right";
            detectionMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "center" })
                .setLngLat([detection.lng, detection.lat])
                .addTo(map);
            detectionPopupRef.current = new maplibregl.Popup({ anchor, offset: 12, closeButton: false, closeOnClick: false,
                className: "custom-popup" })
                .setLngLat([detection.lng, detection.lat])
                .setText(popupText || "Detection")
                .addTo(map);

            // Frame the watched perimeter and the detection, with room for the label.
            const bounds = new maplibregl.LngLatBounds();
            getImageCoordinates(points[0].lat, points[0].lng, circleRadius).forEach((c) => bounds.extend(c));
            bounds.extend([detection.lng, detection.lat]);
            // Extra room only on the side where the label is drawn.
            const padding = { top: 30, bottom: 30, left: 30, right: 30 };
            const labelSide = { bottom: "top", top: "bottom", left: "right", right: "left" }[anchor];
            padding[labelSide] = labelSide === "top" || labelSide === "bottom" ? 70 : 140;
            map.fitBounds(bounds, { padding, maxZoom: 18 });
        };

        runWhenStyleReady("detection", update);
    }, [mapType, points, circleRadius, hasDetection, detection?.lat, detection?.lng, detection?.distance, detection?.label]);

    // Fonction pour reset la vue de la carte.
    const resetView = () => {
        const map = mapRef.current;
        if (!map || points.length === 0) return;

        if (points.length > 1) {
            if (initialBounds) {
                map.fitBounds(initialBounds, { padding: 50 });
            }
        } else if (points.length === 1) {
            if (mapType === "turbineDashboard") {
                const imageCoords = getImageCoordinates(points[0].lat, points[0].lng, circleRadius);
                const imageBounds = new maplibregl.LngLatBounds();
                imageCoords.forEach(([lng, lat]) => imageBounds.extend([lng, lat]));
                imageBounds.extend([points[0].lng, points[0].lat]);
                map.fitBounds(imageBounds, { padding: 20 });
            } else {
                map.flyTo({
                    center: [points[0].lng, points[0].lat],
                    zoom: getZoomForRadius(circleRadius),
                });
            }
        }
    };

    // Rendu JSX du composant.
    return (
        <div style={{ height: "32vh", width: "100%", borderRadius: "15px", overflow: "hidden", position: "relative" }}>
            <div ref={mapContainer} style={{ height: "100%", width: "100%" }} />
            <button
                onClick={resetView}
                style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    zIndex: 10,
                    padding: "6px 5px",
                    backgroundColor: "#fff",
                    border: "2px solid rgba(0,0,0,0.3)",
                    borderRadius: "4px",
                    cursor: "pointer",
                }}
            >
                <span className="material-icons">refresh</span>
            </button>
        </div>
    );
});

export default MDMapLibre;